import { SystemStyleObject } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IQuoteRespSchema, QuoteRequestSchema } from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import clamp from 'lodash/clamp';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdEdit } from 'react-icons/md';
import {
  MButton,
  MFlex,
  MHeading,
  MIcon,
  MInput,
} from '../../../../components/Monetize';
import { useExperienceConfiguration } from '../../../../hooks';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import { QuoteDataTypes } from '../../../../hooks/useQuote';
import { useAuth } from '../../../../services/auth0';
import { logger } from '../../../../services/logger';

const CONTAINER_FOCUS_PROPS: SystemStyleObject = {
  background: 'tGray.support',
  borderColor: 'tGray.lightPurple',
};

const FormSchema = QuoteRequestSchema.pick({ description: true });

const calculateInputWidth = (text: string, min = 220) => {
  if (!text) {
    return min;
  }
  return clamp(text.length * 8, min, 500);
};

interface QuoteDescriptionEditorProps extends Pick<
  QuoteDataTypes,
  'setQuote' | 'updateQuote'
> {
  quote: IQuoteRespSchema;
  isReadOnly: boolean;
}

export const QuoteDescriptionEditor = ({
  quote,
  setQuote,
  updateQuote,
  isReadOnly,
}: QuoteDescriptionEditorProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { quoteNameEditableRoles } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { currentTenantUserHasRole } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [quoteDescription, setQuoteDescription] = useState(quote.description);
  const [inputWidth, setInputWidth] = useState(() =>
    calculateInputWidth(quote.description),
  );
  const isCancellingRef = useRef(false);

  useEffect(() => {
    setQuoteDescription(quote.description);
    setInputWidth(calculateInputWidth(quote.description));
  }, [quote.description]);

  useNonInitialEffect(() => {
    setIsEditing(false);
    reset({ description: quoteDescription });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.status]);

  const { control, reset, handleSubmit } = useForm({
    defaultValues: { description: quoteDescription },
    resolver: zodResolver(FormSchema),
  });

  const isDisabled =
    isReadOnly || !currentTenantUserHasRole(quoteNameEditableRoles);

  function handleBlur(e: React.FocusEvent) {
    if (!isEditing) {
      return;
    }

    const nextFocused = e.relatedTarget as Node | null;

    if (!formRef.current) {
      return;
    }

    // If focus moved to something outside the form → save the change
    if (!nextFocused || !formRef.current.contains(nextFocused)) {
      if (isCancellingRef.current) {
        isCancellingRef.current = false;
        return;
      }
      submitForm();
    }
  }

  function handleReset() {
    isCancellingRef.current = true;
    setIsEditing(false);
    reset({ description: quoteDescription });
  }

  async function onSubmit(data: { description: string }) {
    try {
      const trimmedDescription = data.description.trim();
      if (quote.description?.trim() === trimmedDescription) {
        setIsEditing(false);
        return;
      }
      setIsEditing(false);
      const updatedQuote = QuoteRequestSchema.parse({
        ...quote,
        description: trimmedDescription,
      });
      setQuote({ ...quote, description: trimmedDescription });
      const response = await updateQuote(
        quote.id,
        QuoteRequestSchema.parse(updatedQuote),
      );
      if (response) {
        setQuoteDescription(response.description);
        // Update cache so popover has correct state on next open without needing a refetch
        reset({ description: response.description });
      }
    } catch (ex) {
      logger.error('Failed to update quote description', ex);
    }
  }

  const submitForm = handleSubmit(onSubmit, (errors) => {
    logger.error('Validation failed', errors);
    setIsEditing(false);
    reset({ description: quoteDescription });
  });

  return (
    <form
      onSubmit={submitForm}
      ref={formRef}
      onBlur={handleBlur}
      tabIndex={-1} // important: allows the form to receive focus events
    >
      <MFlex alignItems="center" minW="100px">
        <Controller
          defaultValue=""
          name="description"
          control={control}
          render={({ field: { value, ref, ...rest } }) => (
            <>
              {/*
              Simulate an input field on hover
              Show an edit icon so the user can discover the editability of the field
              When in edit mode, show the input field and hide the edit icon
             */}
              {(isDisabled || !isEditing) && (
                <MButton
                  type="button"
                  variant="bare"
                  borderColor="transparent"
                  borderRadius="3px"
                  p="0"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="nowrap"
                  isDisabled={isDisabled}
                  _disabled={{
                    cursor: 'default',
                    opacity: 1,
                  }}
                  _hover={isDisabled ? {} : CONTAINER_FOCUS_PROPS}
                  _focusWithin={isDisabled ? {} : CONTAINER_FOCUS_PROPS}
                  onClick={() => {
                    reset({ description: quoteDescription });
                    setIsEditing(true);
                  }}
                >
                  <MHeading
                    fontSize="md"
                    fontWeight="600"
                    px="2"
                    py="1"
                    w="100%"
                    lineClamp={2}
                  >
                    {value || quote.description}
                  </MHeading>
                  {!isDisabled && !isEditing && (
                    <MIcon as={MdEdit} boxSize="4" />
                  )}
                </MButton>
              )}
              {!isDisabled && isEditing && (
                <MInput
                  width={inputWidth}
                  autoFocus
                  value={value}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      handleReset();
                    }
                  }}
                  {...rest}
                />
              )}
            </>
          )}
        />
      </MFlex>
    </form>
  );
};
